/**
 * usePagination — Generic client-side pagination hook.
 *
 * Works with any array. Returns the current page slice plus
 * navigation helpers. Resets to page 1 when items or pageSize change.
 *
 * @example
 *   const { page, pageItems, totalPages, setPage, next, prev } =
 *     usePagination(users, { pageSize: 20 });
 */
import { useState, useMemo, useCallback, useEffect } from 'react';

export interface PaginationOptions {
  /** Items per page. Defaults to 20. */
  pageSize?: number;
  /** Starting page (1-based). Defaults to 1. */
  initialPage?: number;
}

export interface PaginationResult<T> {
  /** Current 1-based page number. */
  page: number;
  /** Items on the current page. */
  pageItems: T[];
  /** Total number of pages. */
  totalPages: number;
  /** Total number of items. */
  totalItems: number;
  /** Current page size. */
  pageSize: number;
  /** Whether a previous page exists. */
  hasPrev: boolean;
  /** Whether a next page exists. */
  hasNext: boolean;
  /** Jump to a specific page. */
  setPage: (page: number) => void;
  /** Go to the next page. */
  next: () => void;
  /** Go to the previous page. */
  prev: () => void;
  /** Go to the first page. */
  first: () => void;
  /** Go to the last page. */
  last: () => void;
  /** 0-based start index of the current page within the full list. */
  startIndex: number;
  /** 0-based end index (exclusive) of the current page within the full list. */
  endIndex: number;
}

export function usePagination<T>(
  items: T[],
  options: PaginationOptions = {},
): PaginationResult<T> {
  const { pageSize = 20, initialPage = 1 } = options;
  const [page, setPageRaw] = useState(initialPage);

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Clamp page when items/pageSize change
  useEffect(() => {
    setPageRaw((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  // Reset to page 1 when the item count changes significantly (e.g. filter applied)
  const prevCountRef = useMemo(() => ({ current: totalItems }), []);
  useEffect(() => {
    if (prevCountRef.current !== totalItems) {
      setPageRaw(1);
      prevCountRef.current = totalItems;
    }
  }, [totalItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const pageItems = useMemo(
    () => items.slice(startIndex, endIndex),
    [items, startIndex, endIndex],
  );

  const setPage = useCallback(
    (p: number) => setPageRaw(Math.max(1, Math.min(p, totalPages))),
    [totalPages],
  );

  const next = useCallback(() => setPage(page + 1), [page, setPage]);
  const prev = useCallback(() => setPage(page - 1), [page, setPage]);
  const first = useCallback(() => setPage(1), [setPage]);
  const last = useCallback(() => setPage(totalPages), [setPage, totalPages]);

  return {
    page,
    pageItems,
    totalPages,
    totalItems,
    pageSize,
    hasPrev: page > 1,
    hasNext: page < totalPages,
    setPage,
    next,
    prev,
    first,
    last,
    startIndex,
    endIndex,
  };
}
